import React from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane, FaArrowDown, FaCode, FaLaptopCode, FaPhp } from 'react-icons/fa';

const Hero: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut" as const,
      },
    },
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-50 pt-20 sm:pt-24 pb-28 sm:pb-20">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-10 w-20 h-20 bg-blue-200 rounded-full opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute top-40 right-20 w-16 h-16 bg-cyan-200 rounded-full opacity-20"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-1/4 w-12 h-12 bg-purple-200 rounded-full opacity-20"
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center"
        >
          {/* Profile Image */}
          <motion.div
            variants={itemVariants}
            className="relative mb-8 inline-block px-2"
          >
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="relative"
            >
              <div className="w-36 h-36 sm:w-44 sm:h-44 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl">
                <img
                  src="/profile.jpg"
                  alt="Mohamad Azka Julda Suparman"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              </div>
            </motion.div>
          </motion.div>

          {/* Greeting */}
          <motion.div
            variants={itemVariants}
            className="mb-4"
          >
            <span className="text-lg text-gray-600 font-medium">Hello, I'm</span>
          </motion.div>

          {/* Name */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 px-2"
          >
            <span className="gradient-text">Mohamad Azka</span>
            <br />
            <span className="text-gray-800">Julda Suparman</span>
          </motion.h1>

          {/* Title */}
          <motion.div
            variants={itemVariants}
            className="mb-8"
          >
            <h2 className="text-2xl md:text-3xl text-gray-600 font-semibold mb-2">
              Software Engineer
            </h2>
            <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto px-4">
              Creating beautiful, responsive, and interactive web experiences with modern technologies
            </p>
          </motion.div>

          {/* Tech Icons */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center space-x-4 sm:space-x-6 mb-8 px-2"
          >
            <motion.div
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-lg"
            >
              <FaCode className="text-blue-600" />
              <span className="text-sm font-medium">React</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.2, rotate: -5 }}
              className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-lg"
            >
              <FaLaptopCode className="text-cyan-600" />
              <span className="text-sm font-medium">TypeScript</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-lg"
            >
              <FaPhp className="text-purple-600" />
              <span className="text-sm font-medium">Php</span>
            </motion.div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-2"
          >
            <motion.a
              href="#projects"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <span>View My Work</span>
              <FaArrowDown className="animate-bounce" />
            </motion.a>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <span>Contact Me</span>
              <FaPaperPlane className="animate-bounce" />
            </motion.a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="absolute bottom-8 left-[43%] md:left-[48.5%] transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center text-gray-400"
        >
          <span className="text-sm mb-2">Scroll Down</span>
          <FaArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;

